import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, Mail } from "lucide-react";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [infoDropdownOpen, setInfoDropdownOpen] = useState(false);
  const location = useLocation();
  const servicesDropdownRef = useRef(null);
  const infoDropdownRef = useRef(null);

  useEffect(() => {
    // Hent ydelser dynamisk fra CMS til dropdown-menuen
    fetch("https://api.dev.auxo.dk/public/content/services")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setServices(data);
        }
      })
      .catch((err) => console.error("Fejl ved hentning af ydelser:", err));
  }, []);

  // Luk dropdowns når man klikker udenfor
  useEffect(() => {
    function handleClickOutside(event) {
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setServicesDropdownOpen(false);
      }
      if (infoDropdownRef.current && !infoDropdownRef.current.contains(event.target)) {
        setInfoDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Luk mobilmenu ved ruteskift
  useEffect(() => {
    setIsOpen(false);
    setServicesDropdownOpen(false);
    setInfoDropdownOpen(false);
  }, [location]);

  const isActive = (path) => location.pathname === path;
  const isServiceActive = (slug) => location.pathname === `/arbejdsopgaver/${slug}`;

  const isAnyServiceActive = services.some((s) => isServiceActive(s.slug_name));
  const isInfoActive = isActive("/medarbejdere") || isActive("/godt-at-vide");

  return (
    <header className="sticky top-0 z-50 w-full border-b border-brand-neutral-border bg-white shadow-sm">
      {/* Top bar med kontaktinfo */}
      <div className="bg-brand-blue text-white py-2 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl flex flex-wrap justify-between items-center text-xs sm:text-sm gap-2">
          <div className="flex items-center gap-4">
            <a
              href="tel:+4523287183"
              className="flex items-center gap-1.5 hover:text-white/70 transition-colors whitespace-nowrap"
            >
              <Phone className="h-4 w-4 text-white" />
              <span className="whitespace-nowrap">23 28 71 83</span>
            </a>
            <a
              href="mailto:mb@bmttag.dk"
              className="flex items-center gap-1.5 hover:text-white/70 transition-colors whitespace-nowrap"
            >
              <Mail className="h-4 w-4 text-white" />
              <span className="whitespace-nowrap">mb@bmttag.dk</span>
            </a>
          </div>
          <div className="hidden md:flex items-center gap-4 text-slate-300 text-xs">
            <span>Åkirkebyvej 4, 7400 Herning</span>
            <span>|</span>
            <span>CVR-nr: 45546845</span>
          </div>
        </div>
      </div>

      {/* Hovednavigation */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img
              src="https://assets.auxodev.dk/assets/306cd3bb-e6f1-472d-9f9e-c228e4c6c6b6/logo.svg"
              alt="BMT TAG ApS Logo"
              className="h-10 sm:h-12 w-auto logo-filter"
            />
          </Link>

          {/* Desktop Navigation (Skjules under 1200px / xl breakpoint i Tailwind er 1280px, men vi kan bruge en custom class eller xl:flex) */}
          <nav className="hidden xl:flex items-center gap-8">
            <Link
              to="/"
              className={`inline-flex items-center text-sm font-semibold pb-1 border-b-2 transition-all duration-200 h-7 ${
                isActive("/")
                  ? "text-brand-accent border-brand-accent"
                  : "text-brand-blue-dark border-transparent hover:text-brand-accent hover:border-brand-accent"
              }`}
            >
              Hjem
            </Link>

            <Link
              to="/privat"
              className={`inline-flex items-center text-sm font-semibold pb-1 border-b-2 transition-all duration-200 h-7 ${
                isActive("/privat")
                  ? "text-brand-accent border-brand-accent"
                  : "text-brand-blue-dark border-transparent hover:text-brand-accent hover:border-brand-accent"
              }`}
            >
              Privat
            </Link>

            <Link
              to="/erhverv"
              className={`inline-flex items-center text-sm font-semibold pb-1 border-b-2 transition-all duration-200 h-7 ${
                isActive("/erhverv")
                  ? "text-brand-accent border-brand-accent"
                  : "text-brand-blue-dark border-transparent hover:text-brand-accent hover:border-brand-accent"
              }`}
            >
              Erhverv
            </Link>

            {/* Dropdown til Arbejdsopgaver */}
            <div className="relative" ref={servicesDropdownRef}>
              <button
                onClick={() => {
                  setServicesDropdownOpen(!servicesDropdownOpen);
                  setInfoDropdownOpen(false);
                }}
                className={`inline-flex items-center gap-1 text-sm font-semibold pb-1 border-b-2 transition-all duration-200 h-7 cursor-pointer ${
                  isAnyServiceActive
                    ? "text-brand-accent border-brand-accent"
                    : "text-brand-blue-dark border-transparent hover:text-brand-accent hover:border-brand-accent"
                }`}
              >
                Arbejdsopgaver
                <ChevronDown className={`h-4 w-4 transition-transform ${servicesDropdownOpen ? "rotate-180" : ""}`} />
              </button>

              {servicesDropdownOpen && (
                <div className="absolute left-0 mt-2 w-64 rounded-md border border-brand-neutral-border bg-white py-2 shadow-lg ring-1 ring-black/5 z-50">
                  {services.length > 0 ? (
                    services.map((service) => (
                      <Link
                        key={service.id}
                        to={`/arbejdsopgaver/${service.slug_name}`}
                        className={`block px-4 py-2 text-sm transition-colors hover:bg-brand-neutral-light hover:text-brand-accent ${
                          isServiceActive(service.slug_name) ? "bg-brand-neutral-light text-brand-accent font-medium" : "text-brand-blue-dark"
                        }`}
                      >
                        {service.title.da}
                      </Link>
                    ))
                  ) : (
                    <span className="block px-4 py-2 text-sm text-slate-400">Indlæser...</span>
                  )}
                </div>
              )}
            </div>

            {/* Dropdown til Praktisk info */}
            <div className="relative" ref={infoDropdownRef}>
              <button
                onClick={() => {
                  setInfoDropdownOpen(!infoDropdownOpen);
                  setServicesDropdownOpen(false);
                }}
                className={`inline-flex items-center gap-1 text-sm font-semibold pb-1 border-b-2 transition-all duration-200 h-7 cursor-pointer ${
                  isInfoActive
                    ? "text-brand-accent border-brand-accent"
                    : "text-brand-blue-dark border-transparent hover:text-brand-accent hover:border-brand-accent"
                }`}
              >
                Praktisk info
                <ChevronDown className={`h-4 w-4 transition-transform ${infoDropdownOpen ? "rotate-180" : ""}`} />
              </button>

              {infoDropdownOpen && (
                <div className="absolute left-0 mt-2 w-64 rounded-md border border-brand-neutral-border bg-white py-2 shadow-lg ring-1 ring-black/5 z-50">
                  <Link
                    to="/medarbejdere"
                    className={`block px-4 py-2 text-sm transition-colors hover:bg-brand-neutral-light hover:text-brand-accent ${
                      isActive("/medarbejdere") ? "bg-brand-neutral-light text-brand-accent font-medium" : "text-brand-blue-dark"
                    }`}
                  >
                    Medarbejdere
                  </Link>
                  <a
                    href="https://derbigum.dk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-4 py-2 text-sm transition-colors hover:bg-brand-neutral-light hover:text-brand-accent text-brand-blue-dark"
                  >
                    Garanti
                  </a>
                  <Link
                    to="/godt-at-vide"
                    className={`block px-4 py-2 text-sm transition-colors hover:bg-brand-neutral-light hover:text-brand-accent ${
                      isActive("/godt-at-vide") ? "bg-brand-neutral-light text-brand-accent font-medium" : "text-brand-blue-dark"
                    }`}
                  >
                    Info
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/kontakt"
              className={`inline-flex items-center text-sm font-semibold pb-1 border-b-2 transition-all duration-200 h-7 ${
                isActive("/kontakt")
                  ? "text-brand-accent border-brand-accent"
                  : "text-brand-blue-dark border-transparent hover:text-brand-accent hover:border-brand-accent"
              }`}
            >
              Kontakt
            </Link>
          </nav>

          {/* Højre side CTA (Desktop) */}
          <div className="hidden xl:block">
            <Link
              to="/indhent-tilbud"
              className="inline-flex items-center justify-center rounded-md bg-brand-accent px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-accent-hover cursor-pointer"
            >
              Indhent tilbud
            </Link>
          </div>

          {/* Burgermenu-knap (Vises under 1200px / xl breakpoint) */}
          <div className="flex xl:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center rounded-md p-2 text-brand-blue-dark hover:bg-brand-neutral-light hover:text-brand-accent transition-colors cursor-pointer"
              aria-expanded="false"
            >
              <span className="sr-only">Åbn hovedmenu</span>
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobil Navigation (Vises kun når burgermenuen er åben og under 1200px) */}
      {isOpen && (
        <div className="xl:hidden border-t border-brand-neutral-border bg-white px-4 py-4 shadow-inner max-h-[calc(100vh-100px)] overflow-y-auto pb-12">
          <div className="space-y-2">
            <Link
              to="/"
              className={`block rounded-md px-3 py-2 text-base font-semibold transition-colors hover:bg-brand-neutral-light hover:text-brand-accent ${
                isActive("/") ? "bg-brand-neutral-light text-brand-accent" : "text-brand-blue-dark"
              }`}
            >
              Hjem
            </Link>

            <Link
              to="/privat"
              className={`block rounded-md px-3 py-2 text-base font-semibold transition-colors hover:bg-brand-neutral-light hover:text-brand-accent ${
                isActive("/privat") ? "bg-brand-neutral-light text-brand-accent" : "text-brand-blue-dark"
              }`}
            >
              Privat
            </Link>

            <Link
              to="/erhverv"
              className={`block rounded-md px-3 py-2 text-base font-semibold transition-colors hover:bg-brand-neutral-light hover:text-brand-accent ${
                isActive("/erhverv") ? "bg-brand-neutral-light text-brand-accent" : "text-brand-blue-dark"
              }`}
            >
              Erhverv
            </Link>

            {/* Mobil dropdown til Arbejdsopgaver */}
            <div className="space-y-1">
              <div className="px-3 py-2 text-base font-semibold text-brand-blue-dark border-b border-brand-neutral-border/50 pb-1">
                Arbejdsopgaver
              </div>
              <div className="pl-4 space-y-1">
                {services.length > 0 ? (
                  services.map((service) => (
                    <Link
                      key={service.id}
                      to={`/arbejdsopgaver/${service.slug_name}`}
                      className={`block rounded-md px-3 py-2 text-sm transition-colors hover:bg-brand-neutral-light hover:text-brand-accent ${
                        isServiceActive(service.slug_name) ? "bg-brand-neutral-light text-brand-accent font-medium" : "text-brand-blue-dark"
                      }`}
                    >
                      {service.title.da}
                    </Link>
                  ))
                ) : (
                  <span className="block px-3 py-2 text-sm text-slate-400">Indlæser...</span>
                )}
              </div>
            </div>

            {/* Mobil dropdown til Praktisk info */}
            <div className="space-y-1">
              <div className="px-3 py-2 text-base font-semibold text-brand-blue-dark border-b border-brand-neutral-border/50 pb-1">
                Praktisk info
              </div>
              <div className="pl-4 space-y-1">
                <Link
                  to="/medarbejdere"
                  className={`block rounded-md px-3 py-2 text-sm transition-colors hover:bg-brand-neutral-light hover:text-brand-accent ${
                    isActive("/medarbejdere") ? "bg-brand-neutral-light text-brand-accent font-medium" : "text-brand-blue-dark"
                  }`}
                >
                  Medarbejdere
                </Link>
                <a
                  href="https://derbigum.dk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block rounded-md px-3 py-2 text-sm transition-colors hover:bg-brand-neutral-light hover:text-brand-accent text-brand-blue-dark"
                >
                  Garanti
                </a>
                <Link
                  to="/godt-at-vide"
                  className={`block rounded-md px-3 py-2 text-sm transition-colors hover:bg-brand-neutral-light hover:text-brand-accent ${
                    isActive("/godt-at-vide") ? "bg-brand-neutral-light text-brand-accent font-medium" : "text-brand-blue-dark"
                  }`}
                >
                  Info
                </Link>
              </div>
            </div>

            <Link
              to="/kontakt"
              className={`block rounded-md px-3 py-2 text-base font-semibold transition-colors hover:bg-brand-neutral-light hover:text-brand-accent ${
                isActive("/kontakt") ? "bg-brand-neutral-light text-brand-accent" : "text-brand-blue-dark"
              }`}
            >
              Kontakt
            </Link>

            <div className="pt-4 border-t border-brand-neutral-border/50">
              <Link
                to="/indhent-tilbud"
                className="block w-full text-center rounded-md bg-brand-accent py-3 text-base font-semibold text-white shadow-sm transition-colors hover:bg-brand-accent-hover cursor-pointer"
              >
                Indhent tilbud
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
